import { STAGE_LABEL, nextStage, type InputType, type Stage } from './stage';

/**
 * The bench a new case opens at. Section 4 of the request of 20 September 2026.
 *
 * Every case used to be created at `intake`. Intake is a bench only for somebody
 * holding the intake permission or carrying the operator flag, so a case booked
 * in by reception sat at a station reception could not work, and the bench that
 * should have had it could not see it. The client's words: do not send them to
 * Intake to get stuck.
 *
 * The default is the first stage AFTER Intake on the case's OWN route, which is
 * CAD / CAM for a digital case and Plaster / Model for an impression one. The
 * client said CAD / CAM; sending an impression there would put it on a bench that
 * cannot start it, because the model has to be poured first, so the route decides
 * and the form shows which bench it will be.
 */
export function defaultStartStage(
  input: InputType,
  product: string,
  category?: string,
): Stage {
  // Intake is first on every route and never its last stage, so the stage after
  // it always exists.
  return nextStage('intake', input, product, category) as Stage;
}

/**
 * Where a case may NOT begin: the two stages that are ends rather than benches.
 *
 * Intake is deliberately still allowed. It remains a stage, a bench and part of
 * every route, for a case that genuinely needs receiving work before it starts,
 * and the selector can still choose it.
 */
export const START_STAGE_REFUSAL: Partial<Record<Stage, string>> = {
  ready: `${STAGE_LABEL.ready} is not a tray a case can start at`,
  finalcheck: `A case cannot start at ${STAGE_LABEL.finalcheck}.`,
};

/** The refusal for a chosen start stage, or null when it is allowed. */
export function startStageRefusal(stage: Stage): string | null {
  return START_STAGE_REFUSAL[stage] ?? null;
}

/** The stages the intake form offers, in production order. */
export const START_STAGES: readonly Stage[] = (
  ['intake', 'cadcam', 'plaster', 'wax', 'ceramic'] as const
).filter((stage) => startStageRefusal(stage) === null);
